"use client";

/**
 * EventSeriesPills — date-navigator chips for Polymarket recurring
 * series ("Bitcoin above ___ on May Y?", weekly weather/crypto markets,
 * etc.). Mirrors PM's own UI: a `Past ▾` dropdown (resolved siblings),
 * up to N inline future chips, and a `More ▾` overflow when the series
 * keeps going. Active chip = currentSlug.
 *
 * Data: /api/markets/event-series/:seriesId (backend cached 60s + edge).
 * The fetch is lazy from the client because the event page already SSR'd
 * the hero/chart/grid by the time this paints — no point blocking the
 * critical path on a sibling list that exists purely for navigation.
 *
 * UI: clean / functional. Bordes solo donde aportan, no neón. Active
 * pill = filled accent; others = neutral chip with hover bump.
 */

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { FiChevronDown } from "react-icons/fi";

interface SeriesEntry {
  slug: string;
  title: string;
  endDate: string | null;
  closed: boolean;
}

interface SeriesPayload {
  upcoming: SeriesEntry[];
  past: SeriesEntry[];
}

interface Props {
  seriesId: string;
  currentSlug: string;
  /** Max inline upcoming pills before the "More" dropdown takes over. */
  maxInline?: number;
}

const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  return `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
}

function isMatchingSlug(slug: string, current: string): boolean {
  return slug === current;
}

/** Is the pill's resolution date the *current calendar day*? Used to
 *  anchor the red dot on today's market regardless of which day the
 *  user is currently viewing. UTC comparison — PM endDates are
 *  resolution-time in UTC (e.g. May 28 16:00Z) and the date portion is
 *  what labels the pill. */
function isPillToday(iso: string | null): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return false;
  const now = new Date();
  return d.getUTCFullYear() === now.getUTCFullYear()
    && d.getUTCMonth() === now.getUTCMonth()
    && d.getUTCDate() === now.getUTCDate();
}

// Module-scope cache keyed by seriesId. When the user navigates between
// sibling pills (May 28 → May 29 → …) the page remounts, but the series
// list is identical across all of them. Without this cache the
// component re-fetches and renders `null` for ~50-200 ms on every nav,
// which made the pills row blink out every time. Keeping the previous
// response in a module-level Map lets the new mount paint with cached
// data on the first frame, then the silent background fetch refreshes
// it once the network roundtrip completes.
const seriesCache = new Map<string, SeriesPayload>();

export default function EventSeriesPills({
  seriesId,
  currentSlug,
  maxInline = 4,
}: Props) {
  const [data, setData] = useState<SeriesPayload | null>(
    () => seriesCache.get(seriesId) ?? null,
  );
  const [pastOpen, setPastOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const pastRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!seriesId) return;
    // Re-hydrate from cache when seriesId changes (e.g. unlikely cross-
    // series navigation) so the new pills paint instantly too.
    const cached = seriesCache.get(seriesId);
    if (cached) setData(cached);

    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/markets/event-series/${seriesId}`);
        if (!r.ok || cancelled) return;
        const json = (await r.json()) as SeriesPayload;
        if (!cancelled) {
          seriesCache.set(seriesId, json);
          setData(json);
        }
      } catch {
        /* silent — pills are nav sugar, never a blocker */
      }
    })();
    return () => { cancelled = true; };
  }, [seriesId]);

  // Close dropdowns on outside click / Escape.
  useEffect(() => {
    if (!pastOpen && !moreOpen) return;
    const onPointer = (e: MouseEvent) => {
      const t = e.target as Node;
      if (pastRef.current && !pastRef.current.contains(t)) setPastOpen(false);
      if (moreRef.current && !moreRef.current.contains(t)) setMoreOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setPastOpen(false); setMoreOpen(false); }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [pastOpen, moreOpen]);

  if (!data) return null;
  const inline = data.upcoming.slice(0, maxInline);
  const overflow = data.upcoming.slice(maxInline);
  // If the upcoming list is empty and there's nothing in Past either we
  // bail — pills with no data add visual noise.
  if (inline.length === 0 && data.past.length === 0) return null;

  return (
    // `flex-wrap` (not `overflow-x-auto`) on purpose — the latter clipped
    // the Past/More dropdowns because their absolute-positioned panels
    // sat inside the scroll container's clipping box. With wrap, the
    // pills flow to a second line on narrow screens and the dropdowns
    // can extend freely.
    <div className="flex flex-wrap items-center gap-2 px-4 sm:px-5 py-2.5">
      {/* Past dropdown — only when there's at least one resolved sibling. */}
      {data.past.length > 0 && (
        <div className="relative" ref={pastRef}>
          <button
            onClick={() => { setPastOpen((v) => !v); setMoreOpen(false); }}
            className={`inline-flex items-center gap-1 h-[26px] px-2.5 rounded-[5px] text-xs font-medium transition-colors border border-gray-200 dark:border-[#1A1A1A] ${
              pastOpen
                ? "bg-gray-100 dark:bg-[#1A1A1A] text-gray-900 dark:text-white"
                : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#0a0a0b]/50"
            }`}
          >
            Past
            <FiChevronDown className={`w-3 h-3 transition-transform ${pastOpen ? "rotate-180" : ""}`} />
          </button>
          {pastOpen && (
            <div className="absolute left-0 top-[calc(100%+4px)] z-20 min-w-[140px] max-h-[280px] overflow-y-auto bg-white dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] shadow-lg py-1">
              {data.past.map((e) => (
                <Link
                  key={e.slug}
                  href={`/polymarket/event/${e.slug}`}
                  className="block px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] transition-colors whitespace-nowrap"
                  onClick={() => setPastOpen(false)}
                >
                  {formatDate(e.endDate)}, {new Date(e.endDate || 0).getFullYear()}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Inline upcoming pills.
          - "Active" (filled gray/white) = the day the user is viewing
            (`currentSlug` match).
          - Red dot = the day that resolves *today* (UTC calendar match).
            These two are independent — when the user navigates to May 29
            the active pill moves to May 29 but the red dot stays anchored
            on May 28 (today). PM behaves the same way. */}
      {inline.map((e) => {
        const active = isMatchingSlug(e.slug, currentSlug);
        const today = isPillToday(e.endDate);
        const dot = today ? (
          <span
            className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0"
            aria-label="Today's market"
          />
        ) : null;
        return active ? (
          <span
            key={e.slug}
            className="inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-[5px] text-xs font-semibold bg-gray-900 dark:bg-white text-white dark:text-black whitespace-nowrap"
          >
            {dot}
            {formatDate(e.endDate)}
          </span>
        ) : (
          <Link
            key={e.slug}
            href={`/polymarket/event/${e.slug}`}
            // Default prefetch (viewport-based in production). The 4
            // inline siblings are always visible so Next.js will prefetch
            // their RSC payload right after mount — clicking them feels
            // instant because the server work is already done. Past +
            // More entries stay un-prefetched because they live behind a
            // click; prefetching them eagerly would burn N backend calls
            // per page mount for navigation the user rarely makes.
            className="inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-[5px] text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-[#1A1A1A] hover:bg-gray-50 dark:hover:bg-[#0a0a0b]/50 transition-colors whitespace-nowrap"
          >
            {dot}
            {formatDate(e.endDate)}
          </Link>
        );
      })}

      {/* More dropdown — only when there are more upcoming entries than
          we render inline. Closes on outside click / Escape (effect above). */}
      {overflow.length > 0 && (
        <div className="relative" ref={moreRef}>
          <button
            onClick={() => { setMoreOpen((v) => !v); setPastOpen(false); }}
            className={`inline-flex items-center gap-1 h-[26px] px-2.5 rounded-[5px] text-xs font-medium transition-colors border border-gray-200 dark:border-[#1A1A1A] ${
              moreOpen
                ? "bg-gray-100 dark:bg-[#1A1A1A] text-gray-900 dark:text-white"
                : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#0a0a0b]/50"
            }`}
          >
            More
            <FiChevronDown className={`w-3 h-3 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
          </button>
          {moreOpen && (
            <div className="absolute right-0 top-[calc(100%+4px)] z-20 min-w-[140px] max-h-[280px] overflow-y-auto bg-white dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] shadow-lg py-1">
              {overflow.map((e) => (
                <Link
                  key={e.slug}
                  href={`/polymarket/event/${e.slug}`}
                  className="block px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] transition-colors whitespace-nowrap"
                  onClick={() => setMoreOpen(false)}
                >
                  {formatDate(e.endDate)}, {new Date(e.endDate || 0).getFullYear()}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
